import {
  getMetricDisplayName,
  getMetricUnitByKey,
  getPercentageMetricByKey,
} from '@src/utils/metrics/metricHelpers';
import { metricKeys } from '@pulse/metrics';
import { PRODUCT_MODE_MAP } from '@src/utils/constants';
import { ECO_AI_ALERT_NWR_METRICS } from '../../constants';
import { getLocalTime } from '../../utils';

export const invertProductMap = {
  ei: PRODUCT_MODE_MAP.EI,
  ai: PRODUCT_MODE_MAP.AI,
  ae: PRODUCT_MODE_MAP.APP,
};

export const sensitivityLogColumns = [
  {
    title: 'User',
    width: 100,
    flexGrow: 1,
    resizable: true,
    dataKey: 'user',
    key: 'user',
  },
  {
    title: 'Date',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'date',
    key: 'date',
  },
  {
    title: 'Metric',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'metricName',
    key: 'metricName',
  },
  {
    title: 'Persistence',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'sustain',
    key: 'sustain',
    cellRenderer: (data: any) => (data.cellData ? `${data.cellData} mins` : 'None'),
  },
  {
    title: 'Impacted Unique',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'impactedUnique',
    key: 'impactedUnique',
    cellRenderer: (data: any) => getCellDataBasedOnMetric(data),
  },
  {
    title: 'Pcp Drop',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'sustainPcpDrop',
    key: 'sustainPcpDrop',
    cellRenderer: (data: any) => getPCPDropDataBasedOnMetric(data),
  },
  {
    title: 'Impacted Unique Pct',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'impactedUniquePct',
    key: 'impactedUniquePct',
    cellRenderer: (data: any) => getCellDataBasedOnMetric(data),
  },
  {
    title: 'Warning Persistence',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'warningSustain',
    key: 'warningSustain',
    cellRenderer: (data: any) => (data.cellData ? `${data.cellData} mins` : 'None'),
  },
  {
    title: 'Warning Impacted Unique',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'warningImpactedUnique',
    key: 'warningImpactedUnique',
    cellRenderer: (data: any) => getCellDataBasedOnMetric(data),
  },
  {
    title: 'Warning Unique pct',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'warningImpactedUniquePct',
    key: 'warningImpactedUniquePct',
    cellRenderer: (data: any) => getCellDataBasedOnMetric(data),
  },
  {
    title: 'Warning Pcp Drop',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'warningPcpDrop',
    key: 'warningPcpDrop',
    cellRenderer: (data: any) => getPCPDropDataBasedOnMetric(data),
  },
  {
    title: 'Critical Persistence',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'criticalSustain',
    key: 'criticalSustain',
    cellRenderer: (data: any) => (data.cellData ? `${data.cellData} mins` : 'None'),
  },
  {
    title: 'Critical Impacted Unique',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'criticalImpactedUnique',
    key: 'criticalImpactedUnique',
    cellRenderer: (data: any) => getCellDataBasedOnMetric(data),
  },
  {
    title: 'Critical Unique pct',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'criticalImpactedUniquePct',
    key: 'criticalImpactedUniquePct',
    cellRenderer: (data: any) => getCellDataBasedOnMetric(data),
  },
  {
    title: 'Critical PCP Drop',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'criticalPcpDrop',
    key: 'criticalPcpDrop',
    cellRenderer: (data: any) => getPCPDropDataBasedOnMetric(data),
  },
];

export const sensitivityAppLogColumns = [
  {
    title: 'User',
    width: 100,
    flexGrow: 1,
    resizable: true,
    dataKey: 'user',
    key: 'user',
  },
  {
    title: 'Date',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'date',
    key: 'date',
  },
  {
    title: 'Metric',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'metricName',
    key: 'metricName',
  },
  {
    title: 'Persistence',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'sustain',
    key: 'sustain',
    cellRenderer: (data: any) => (data.cellData ? `${data.cellData} mins` : 'None'),
  },
  {
    title: 'Impacted Unique/Count',
    width: 0,
    flexGrow: 1,
    resizable: true,
    key: 'impactedUnique',
    cellRenderer: ({ rowData }) =>
      showImpactedFlowsFields(rowData) ? rowData.impactedFlows : rowData.impactedUnique,
  },

  {
    title: 'Pcp Drop',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'sustainPcpDrop',
    key: 'sustainPcpDrop',
    cellRenderer: (data: any) => getPCPDropDataBasedOnMetric(data),
  },
  {
    title: 'Impacted Count Pct',
    width: 0,
    flexGrow: 1,
    resizable: true,
    key: 'impactedUniquePct',
    cellRenderer: ({ rowData }) =>
      showImpactedFlowsFields(rowData) ? rowData.impactedFlowsPct : rowData.impactedUniquePct,
  },
  {
    title: 'Warning Persistence',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'warningSustain',
    key: 'warningSustain',
    cellRenderer: (data: any) => (data.cellData ? `${data.cellData} mins` : 'None'),
  },
  {
    title: 'Warning Impacted Unique/Count',
    width: 0,
    flexGrow: 1,
    resizable: true,
    key: 'warningImpactedUnique',
    cellRenderer: ({ rowData }) =>
      showImpactedFlowsFields(rowData)
        ? rowData.warningImpactedFlows
        : rowData.warningImpactedUnique,
  },
  {
    title: 'Warning Impacted Unique/Count pct',
    width: 0,
    flexGrow: 1,
    resizable: true,
    key: 'warningImpactedUniquePct',
    cellRenderer: ({ rowData }) =>
      showImpactedFlowsFields(rowData)
        ? rowData.warningImpactedFlowsPct
        : rowData.warningImpactedUniquePct,
  },
  {
    title: 'Warning Pcp Drop',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'warningPcpDrop',
    key: 'warningPcpDrop',
    cellRenderer: (data: any) => getPCPDropDataBasedOnMetric(data),
  },
  {
    title: 'Critical Persistence',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'criticalSustain',
    key: 'criticalSustain',
    cellRenderer: (data: any) => (data.cellData ? `${data.cellData} mins` : 'None'),
  },
  {
    title: 'Critical Impacted Count',
    width: 0,
    flexGrow: 1,
    resizable: true,
    key: 'criticalImpactedUnique',
    cellRenderer: ({ rowData }) =>
      showImpactedFlowsFields(rowData)
        ? rowData.criticalImpactedFlows
        : rowData.criticalImpactedUnique,
  },
  {
    title: 'Critical Impacted Count pct',
    width: 0,
    flexGrow: 1,
    resizable: true,
    key: 'criticalImpactedUniquePct',
    cellRenderer: ({ rowData }) =>
      showImpactedFlowsFields(rowData)
        ? rowData.criticalImpactedFlowsPct
        : rowData.criticalImpactedUniquePct,
  },
  {
    title: 'Critical PCP Drop',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'criticalPcpDrop',
    key: 'criticalPcpDrop',
    cellRenderer: (data: any) => getPCPDropDataBasedOnMetric(data),
  },
];

const getAdAlertValueWithUnit = (data) => {
  const { metric } = data.rowData;
  if (metric === 'Ad Average Bitrate') {
    return `${data.cellData} mbps`;
  }
  return `${data.cellData} %`;
};

export const sensitivityAdLogColumns = [
  {
    title: 'User',
    width: 100,
    flexGrow: 1,
    resizable: true,
    dataKey: 'user',
    key: 'user',
  },
  {
    title: 'Date',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'date',
    key: 'date',
  },
  {
    title: 'Metric',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'metricName',
    key: 'metricName',
  },
  {
    title: 'Threshold',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'asf',
    key: 'asf',
    cellRenderer: (data: any) => (data.cellData ? getAdAlertValueWithUnit(data) : 'None'),
  },
  {
    title: 'Cumulative Impacted Unique Devices',
    width: 0,
    flexGrow: 1,
    resizable: true,
    dataKey: 'impactedUnique',
    key: 'impactedUnique',
    cellRenderer: (data: any) => (data.cellData ? data.cellData : 'None'),
  },
];

const getPCPDropDataBasedOnMetric = (data) => {
  if (data.rowData?.metricKey !== metricKeys.ConcurrentPlays) {
    return 'NA';
  }
  return data.cellData ? data.cellData : 'None';
};

const getCellDataBasedOnMetric = (data) => {
  if (data.rowData?.metricKey === metricKeys.ConcurrentPlays) {
    return 'NA';
  }
  return data.cellData ? data.cellData : 'None';
};

const showImpactedFlowsFields = (rowData) => {
  const metricKey = rowData?.metricKey;
  const percentageMetric = getPercentageMetricByKey({ metricKey, defaultValue: metricKey });
  return (
    getMetricUnitByKey({ metricKey: percentageMetric, defaultValue: metricKey }) === '%' &&
    !ECO_AI_ALERT_NWR_METRICS.includes(percentageMetric)
  );
};

const formatColumnData = (filteredLogs) => {
  return filteredLogs.map((log) => {
    return {
      ...log,
      metricName: `${getMetricDisplayName({ metricKey: log.metricKey })}${
        log.isDeleted ? ' (Disabled)' : ''
      }`,
      date: getLocalTime(log.updatedAt, null),
    };
  });
};

export const getDataForColumns = (logsData) => {
  const filteredLogs = logsData.filter((log) => log.app === 'ei');
  const formatted = formatColumnData(filteredLogs);
  return formatted.reverse();
};

export const getDataForAppColumns = (logsData) => {
  const filteredLogs = logsData.filter((log) => log.app === 'ae');
  const formatted = formatColumnData(filteredLogs);
  return formatted.reverse();
};

export const getDataForAdColumns = (logsData) => {
  const filteredLogs = logsData.filter((log) => log.app === 'ai');
  const formatted = formatColumnData(filteredLogs);
  return formatted.reverse();
};
